// Vietnamese labels for backend `ActivityAction` enum values.
//
// Used by the dashboard activity feed (and any future activity timeline
// UI). When the backend ships a new action, add the code here so the
// frontend has a localized label; unknown codes fall back to the raw
// code so they're at least visible in the UI.

export type ActivityAction =
  // Property actions
  | "CREATED"
  | "INFO_UPDATED"
  | "PRICE_CHANGED"
  | "STATUS_CHANGED"
  | "IMAGES_UPLOADED"
  | "IMAGES_DELETED"
  | "IMAGES_REORDERED"
  | "CONFIRMED_FRESH"
  | "BULK_IMPORTED"
  | "DELETED"
  // Lead actions
  | "LEAD_CREATED"
  | "LEAD_STATUS_CHANGED"
  | "LEAD_ASSIGNED"
  | "LEAD_NOTE_ADDED"
  // Customer actions
  | "CUSTOMER_CREATED"
  | "CUSTOMER_UPDATED"
  // Appointment actions
  | "APPOINTMENT_CREATED"
  | "APPOINTMENT_RESCHEDULED"
  | "APPOINTMENT_STATUS_CHANGED"
  | "APPOINTMENT_CANCELED"
  // User actions (bucket 3)
  | "USER_INVITED"
  | "USER_DEACTIVATED"
  | "USER_REACTIVATED"
  | "PASSWORD_RESET";

export const activityActionLabels: Record<ActivityAction, string> = {
  CREATED: "đã tạo",
  INFO_UPDATED: "đã cập nhật thông tin",
  PRICE_CHANGED: "đã đổi giá",
  STATUS_CHANGED: "đã đổi trạng thái",
  IMAGES_UPLOADED: "đã tải lên ảnh",
  IMAGES_DELETED: "đã xóa ảnh",
  IMAGES_REORDERED: "đã sắp xếp lại ảnh",
  CONFIRMED_FRESH: "đã xác nhận tin còn",
  BULK_IMPORTED: "đã nhập hàng loạt",
  DELETED: "đã xóa",
  LEAD_CREATED: "đã thêm khách hàng tiềm năng",
  LEAD_STATUS_CHANGED: "đã đổi trạng thái lead",
  LEAD_ASSIGNED: "đã gán môi giới cho lead",
  LEAD_NOTE_ADDED: "đã thêm ghi chú",
  CUSTOMER_CREATED: "đã thêm khách hàng",
  CUSTOMER_UPDATED: "đã cập nhật khách hàng",
  APPOINTMENT_CREATED: "đã đặt lịch hẹn",
  APPOINTMENT_RESCHEDULED: "đã dời lịch hẹn",
  APPOINTMENT_STATUS_CHANGED: "đã đổi trạng thái lịch hẹn",
  APPOINTMENT_CANCELED: "đã hủy lịch hẹn",
  USER_INVITED: "đã mời thành viên",
  USER_DEACTIVATED: "đã tắt tài khoản",
  USER_REACTIVATED: "đã kích hoạt lại tài khoản",
  PASSWORD_RESET: "đã đặt lại mật khẩu",
};

/** Vietnamese label for an entity type (used by the activity feed). */
export const entityTypeLabels: Record<string, string> = {
  PROPERTY: "bất động sản",
  LEAD: "khách hàng tiềm năng",
  CUSTOMER: "khách hàng",
  APPOINTMENT: "lịch hẹn",
};

/**
 * Resolve a localized action label, falling back to the raw code for
 * unknown actions so they remain debuggable.
 */
export function labelForAction(action: string): string {
  return activityActionLabels[action as ActivityAction] ?? action.toLowerCase();
}

/** Resolve a localized entity-type label, falling back to the raw code. */
export function labelForEntityType(entityType: string): string {
  return entityTypeLabels[entityType] ?? entityType.toLowerCase();
}

/**
 * Best-effort link target for an activity item. Returns null when we
 * have no UI to navigate to (e.g. customer detail page exists; property
 * detail page exists; "deleted" entities link to nothing).
 */
export function activityLink(entityType: string, entityId: number): string | null {
  switch (entityType) {
    case "PROPERTY":
      return `/properties/${entityId}`;
    case "LEAD":
      return `/leads`;
    case "CUSTOMER":
      return `/customers/${entityId}`;
    case "APPOINTMENT":
      return `/appointments`;
    default:
      return null;
  }
}
